import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  FormControl,
  FormLabel,
  Autocomplete,
  TextField,
  Chip,
  Paper,
  Grid,
} from '@mui/material';
import { School, TrendingUp, Assessment, Group, CheckCircle, Schedule } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Layout from 'Component/Layouts/layout';
import useApiCaller from 'hooks/apiCaller';
import useCredentials from 'hooks/useCredentials';
import useUserDetails from 'hooks/useUserDetails';
import { COMPANY_ROLES } from 'Constants/roles';
import { fetchCourses } from 'reducer/userCourseDetailSlice';
import 'react-circular-progressbar/dist/styles.css';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────
const TABS = [
  { key: 'progress', label: 'In Progress Courses' },
  { key: 'completed', label: 'Completed Courses' },
  { key: 'topPerformers', label: 'Top Performers' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────
const Course = () => {
  const [status, setStatus] = useState('progress');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [getAllCourses, setGetAllCourses] = useState([]);
  const [topPerformers, setTopPerformers] = useState([]);

  const { userData: user } = useCredentials();
  const { companyId } = useUserDetails();
  const navigate = useNavigate();
  const apiCaller = useApiCaller();
  const dispatch = useDispatch();
  const coursesData = useSelector((state) => state?.userCourseDetail?.coursesData);

  // Filter tabs based on company ID
  const availableTabs =
    companyId === COMPANY_ROLES.SATIN_CREDITCARE_NETWORK_LTD
      ? TABS.filter((tab) => tab.key !== 'topPerformers')
      : TABS;

  // Redirect to in-progress tab if user is on topPerformers tab and company ID is 100
  useEffect(() => {
    if (status === 'topPerformers' && companyId === COMPANY_ROLES.SATIN_CREDITCARE_NETWORK_LTD) {
      setStatus('progress');
    }
  }, [status, companyId]);

  const handleShowCourseDetails = (val) => {
    const { _id, uniqueCourseKeyInEmployeeCourses } = val;
    navigate(`/courses/${_id}/${uniqueCourseKeyInEmployeeCourses}/${status}`);
  };

  useEffect(() => {
    if (user) dispatch(fetchCourses({ apiCaller, status }));
  }, [status, user]);

  useEffect(() => {
    const fetchAllCourses = async () => {
      try {
        const { data } = await apiCaller.get('/super-user/getAllCourse');
        setGetAllCourses(data?.data?.courses || []);
      } catch (err) {
        console.error('Error fetching all courses', err);
      }
    };
    fetchAllCourses();
  }, []);

  const handleSelectChange = async (_, newValue) => {
    if (!newValue || newValue._id === selectedCourse?._id) return;
    setSelectedCourse(newValue);
    try {
      const { data } = await apiCaller.post('/super-user/get-top-performers', {
        courseId: newValue._id,
      });
      setTopPerformers(data?.data?.data || []);
    } catch (err) {
      console.error('Error fetching top performers', err);
    }
  };

  const renderTopPerformers = () => (
    <Box>
      <FormControl fullWidth sx={{ mb: 4 }}>
        <FormLabel
          sx={{
            fontSize: 16,
            fontWeight: 600,
            color: '#1e293b',
            mb: 2,
          }}
        >
          Select Course
        </FormLabel>
        <Autocomplete
          size="medium"
          value={selectedCourse}
          onChange={handleSelectChange}
          options={getAllCourses}
          getOptionLabel={(option) => option.courseTitle || ''}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Choose a course to view top performers"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  '&:hover fieldset': {
                    borderColor: '#3b82f6',
                  },
                },
              }}
            />
          )}
        />
      </FormControl>

      {topPerformers.length > 0 && companyId !== COMPANY_ROLES.SATIN_CREDITCARE_NETWORK_LTD && (
        <Box>
          <Box display="flex" alignItems="center" mb={2}>
            <Box
              sx={{
                p: 1,
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                mr: 1.5,
              }}
            >
              <TrendingUp sx={{ fontSize: 20 }} />
            </Box>
            <Typography variant="h6" fontWeight="600" sx={{ color: '#1e293b' }}>
              Top Performers
            </Typography>
          </Box>

          <Paper
            sx={{
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontFamily: 'inherit',
                }}
              >
                <thead
                  style={{
                    background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                    borderBottom: '2px solid #e2e8f0',
                  }}
                >
                  <tr>
                    <th
                      style={{
                        ...thStyle,
                        fontWeight: '700',
                        color: '#1e293b',
                        fontSize: '13px',
                        textAlign: 'left',
                        padding: '12px',
                      }}
                    >
                      Rank
                    </th>
                    <th
                      style={{
                        ...thStyle,
                        fontWeight: '700',
                        color: '#1e293b',
                        fontSize: '13px',
                        textAlign: 'left',
                        padding: '12px',
                      }}
                    >
                      Employee Name
                    </th>
                    <th
                      style={{
                        ...thStyle,
                        fontWeight: '700',
                        color: '#1e293b',
                        fontSize: '13px',
                        textAlign: 'left',
                        padding: '12px',
                      }}
                    >
                      Score
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {topPerformers.map((emp, index) => (
                    <tr
                      key={emp.employeeId}
                      style={{
                        borderBottom: '1px solid #f1f5f9',
                        '&:hover': {
                          backgroundColor: '#f8fafc',
                        },
                      }}
                    >
                      <td
                        style={{
                          ...tdStyle,
                          padding: '12px',
                          fontWeight: '600',
                          color: '#475569',
                        }}
                      >
                        {index + 1}
                      </td>
                      <td
                        style={{
                          ...tdStyle,
                          padding: '12px',
                          fontWeight: '600',
                          color: '#1e293b',
                        }}
                      >
                        {emp.name}
                      </td>
                      <td
                        style={{
                          ...tdStyle,
                          padding: '12px',
                          fontWeight: '600',
                          color: '#059669',
                        }}
                      >
                        {emp.score}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );

  const renderCoursesGrid = (status, allCourses) => {
    let tabData = [];
    console.log('allCourses', allCourses);
    switch (status) {
      case 'completed':
        tabData = allCourses?.filter((item) => item.courseProgress === 'VIEW COURSE');
        break;

      case 'progress':
        tabData = allCourses?.filter((item) => item.courseProgress === 'START COURSE');
        break;

      default:
        tabData = [...allCourses];
        break;
    }
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(4, 1fr)',
            xl: 'repeat(5, 1fr)',
          },
          gap: { xs: 1.5, sm: 2, md: 2.5, lg: 3 },
          mt: { xs: 1.5, sm: 2 },
          px: { xs: 1, sm: 1.5, md: 2 },
          maxWidth: '100%',
        }}
      >
        {tabData?.map((course) => (
          <CourseCard
            key={course.uniqueCourseKeyInEmployeeCourses ?? course._id}
            course={course}
            onClick={() => handleShowCourseDetails(course)}
          />
        ))}
      </Box>
    );
  };

  return (
    <Layout>
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          p: { xs: 1.5, sm: 2, md: 2.5, lg: 3 },
          maxWidth: '100%',
          overflow: 'hidden',
        }}
      >
        {/* Professional Header */}
        <Box
          sx={{
            mb: 2,
            background: '#ffffff',
            borderRadius: '8px',
            p: 2,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e2e8f0',
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
          >
            <Box>
              <Typography
                variant="h5"
                fontWeight="600"
                sx={{
                  color: '#1e293b',
                  mb: 0.25,
                }}
              >
                My Courses
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontWeight: '400' }}>
                Track your learning progress and manage enrolled courses
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Course Status Tabs */}
        <Card
          sx={{
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            mb: { xs: 2, sm: 2.5, md: 3 },
            maxWidth: '100%',
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: { xs: 0.5, sm: 1, md: 1.5 },
                mb: { xs: 1.5, sm: 2 },
                justifyContent: { xs: 'center', sm: 'flex-start' },
              }}
            >
              {availableTabs.map((tab) => (
                <Chip
                  key={tab.key}
                  label={tab.label}
                  onClick={() => setStatus(tab.key)}
                  sx={{
                    px: { xs: 1.5, sm: 2, md: 2.5 },
                    py: { xs: 0.25, sm: 0.5 },
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    fontWeight: status === tab.key ? 600 : 500,
                    backgroundColor: status === tab.key ? '#3b82f6' : 'transparent',
                    color: status === tab.key ? '#ffffff' : '#64748b',
                    border: status === tab.key ? 'none' : '1px solid #e2e8f0',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    minHeight: { xs: '32px', sm: '36px' },
                    '&:hover': {
                      backgroundColor: status === tab.key ? '#1d4ed8' : 'rgba(59, 130, 246, 0.04)',
                      transform: 'translateY(-1px)',
                      boxShadow:
                        status === tab.key
                          ? '0 2px 8px 0 rgba(59, 130, 246, 0.3)'
                          : '0 1px 4px rgba(0, 0, 0, 0.1)',
                    },
                  }}
                />
              ))}
            </Box>

            <Box>
              {status === 'topPerformers' &&
              companyId !== COMPANY_ROLES.SATIN_CREDITCARE_NETWORK_LTD
                ? renderTopPerformers()
                : renderCoursesGrid(status, coursesData)}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Layout>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Reusable Components
// ─────────────────────────────────────────────────────────────────────────────
const CourseCard = ({ course, onClick }) => (
  <Card
    sx={{
      borderRadius: '12px',
      height: '100%',
      background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
      border: '1px solid #e2e8f0',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      transition: 'all 0.2s ease',
      cursor: 'pointer',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)',
      },
    }}
    onClick={onClick}
  >
    <CardContent
      sx={{
        p: { xs: 1.5, sm: 2 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box display="flex" alignItems="flex-start" mb={{ xs: 1.5, sm: 2 }}>
        <Box
          sx={{
            p: { xs: 0.75, sm: 1 },
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            color: 'white',
            mr: { xs: 1, sm: 1.5 },
            flexShrink: 0,
          }}
        >
          <School sx={{ fontSize: { xs: 18, sm: 20 } }} />
        </Box>
        <Typography
          sx={{
            fontSize: { xs: 14, sm: 16 },
            fontWeight: 600,
            color: '#1e293b',
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {course.courseTitle}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: { xs: 0.75, sm: 1 },
          flex: 1,
          mb: { xs: 1.5, sm: 2 },
        }}
      >
        <Info label="Start Date" value={formatDate(course.startDate)} />
        <Info label="End Date" value={formatDate(course.endDate)} />
        <Info label="Score" value={course.score} />
        <Info label="Attempts" value={course.attempts} />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 'auto' }}>
        <Button
          variant="contained"
          size="small"
          sx={{
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            fontSize: { xs: 11, sm: 12 },
            fontWeight: 600,
            px: { xs: 1.5, sm: 2 },
            py: { xs: 0.75, sm: 1 },
            textTransform: 'none',
            boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
            },
          }}
        >
          {course.courseProgress}
        </Button>
      </Box>
    </CardContent>
  </Card>
);

const Info = ({ label, value, mt }) => (
  <Box sx={{ mb: mt ? { xs: 1, sm: 1.5 } : { xs: 0.25, sm: 0.5 } }}>
    <Typography
      sx={{
        fontSize: { xs: 10, sm: 11 },
        color: '#64748b',
        fontWeight: 500,
        mb: { xs: 0.125, sm: 0.25 },
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: { xs: 12, sm: 13 },
        color: '#1e293b',
        fontWeight: 600,
        lineHeight: 1.2,
      }}
    >
      {value}
    </Typography>
  </Box>
);

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return isNaN(date) ? dateString : date.toLocaleDateString('en-GB');
};

const thStyle = { padding: '8px', borderBottom: '1px solid #ddd' };
const tdStyle = { padding: '8px', borderBottom: '1px solid #eee' };

export default Course;
